import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/Button';
import { Dropdown } from '~/components/Dropdown';
import { IconButton } from '~/components/IconButton';
import { Label } from '~/components/Label';
import { Separator } from '~/components/Separator';
import { cn } from '~/lib/cn';
import { CheckboxTree } from '~/modules/label-perf/components/CheckboxTree';
import type { CheckboxTreeItem } from '~/modules/label-perf/components/CheckboxTree/types';
import { flattenCheckboxTree } from '~/modules/label-perf/components/CheckboxTree/utils';
import { useAvailableDropdownHeight } from '~/modules/label-perf/components/LabelFilter/useAvailableDropdownHeight';
import type {
  LabelData,
  LabelHierarchy,
  LabelPerfFilters,
} from '~/modules/label-perf/types';
import { mapLabelHierarchyToCheckboxTreeItem } from '~/modules/label-perf/utils/labels';

interface FilterPaneProps {
  originalLabels: LabelData[];
  labelHierarchyData: LabelHierarchy[] | undefined;
  isLoading: boolean;
  disabled?: boolean;
  onApplyFilters: (filters: LabelPerfFilters) => void;
  onClose?: () => void;
}

const FilterPanel = forwardRef<HTMLFormElement, FilterPaneProps>(
  (
    {
      onClose,
      onApplyFilters,
      originalLabels,
      labelHierarchyData,
      isLoading,
      disabled,
    },
    ref,
  ) => {
    const { t } = useTranslation('label-perf');
    const methods = useForm<LabelPerfFilters>({
      defaultValues: { labels: originalLabels },
    });

    const {
      watch,
      setValue,
      handleSubmit,
      formState: { isDirty },
    } = methods;

    const labelFlatItems = watch('labels');
    const [labelTreeItems, setLabelTreeItems] = useState<CheckboxTreeItem[]>(
      [],
    );
    const dropdownRef = useRef<HTMLDivElement>(null);
    const { availableHeight } = useAvailableDropdownHeight(dropdownRef);

    const onSubmit: SubmitHandler<LabelPerfFilters> = (formData) => {
      onApplyFilters(formData);
      onClose?.();
    };

    useEffect(() => {
      if (labelHierarchyData?.length) {
        const originalLabelIds = new Set(
          originalLabels.map((label) => label.labelId),
        );

        setLabelTreeItems(
          mapLabelHierarchyToCheckboxTreeItem(
            labelHierarchyData,
            originalLabelIds,
          ),
        );
      }
    }, [labelHierarchyData, originalLabels]);

    useEffect(() => {
      if (isLoading || !labelTreeItems.length) {
        return;
      }

      const newLabelFlatItems = flattenCheckboxTree(labelTreeItems)
        .filter((label) => label.checked)
        .map((label) => ({
          labelId: parseInt(label.id, 10),
          name: label.label,
        }))
        .sort((a, b) => a.labelId - b.labelId);

      setValue('labels', newLabelFlatItems, { shouldDirty: true });
    }, [labelTreeItems, setValue, isLoading]);

    const handleLabelsTreeChange = useCallback(
      (newItems: CheckboxTreeItem[]) => {
        setLabelTreeItems(newItems);
      },
      [],
    );

    return (
      <FormProvider {...methods}>
        <form
          ref={ref}
          onSubmit={handleSubmit(onSubmit)}
          className="flex size-full flex-col"
        >
          {/* HEADER */}
          <div className="flex shrink-0 items-center justify-between p-lg">
            <div className="text-headline-md font-bold">
              {t('body.filters')}
            </div>
            <IconButton
              iconType="xmark"
              iconVariant="regular"
              size="large"
              onClick={onClose}
              className="-mr-sm -mt-sm duration-300 ease-out"
              aria-label={t('config.closePanel', 'Close panel')}
            />
          </div>

          {/* CONTENT */}
          <div className="flex min-h-0 grow flex-col p-lg pt-0">
            <Label className="mb-sm font-semibold">
              {t('body.labelRegion')}
            </Label>

            {isLoading ? (
              <div className="w-full" ref={dropdownRef}>
                <Dropdown options={[]} placeholder={t('body.loading')} disabled>
                  <div data-testid="label-filter-loading-indicator">
                    {t('body.loading')}
                  </div>
                </Dropdown>
              </div>
            ) : (
              <div className="w-full" ref={dropdownRef}>
                <Dropdown
                  options={[]}
                  placeholder={
                    labelFlatItems.length > 0
                      ? t('body.labelsWithCount', {
                          count: labelFlatItems.length,
                        })
                      : t('body.selectLabels')
                  }
                  contentClassName={cn('z-50')}
                  alignment="start"
                  disabled={disabled}
                >
                  <CheckboxTree
                    value={labelTreeItems}
                    setValue={setLabelTreeItems}
                    onChange={handleLabelsTreeChange}
                    style={
                      availableHeight
                        ? { maxHeight: `${availableHeight}px` }
                        : { maxHeight: '50dvh' }
                    }
                  />
                </Dropdown>
              </div>
            )}
          </div>

          {/* FOOTER */}
          <div className="shrink-0">
            <Separator />
            <div className="flex w-full justify-end gap-lg border-t-px px-lg py-md bg-level-03 border-emphasis-subtle">
              <Button
                disabled={!isDirty}
                size="large"
                variant="primary"
                type="submit"
              >
                {t('actions.apply')}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    );
  },
);

FilterPanel.displayName = 'FilterPane';

export { FilterPanel };
